import { CANADA_ALPHA_2 } from 'constants/common';
import { useFormik } from 'formik';
import { taxIDRegex } from 'helpers/validators';
import React from 'react';
import * as Yup from 'yup';

import { useCreateExporters } from 'features/sellers/hooks/useCreateExporters';
import { useUpdateExporter } from 'features/sellers/hooks/useUpdateExporter';

export default function useExporterForm(action = 'create', initialValues = {}, onSuccess, onError) {
  const { mutate: createExporter, isLoading: isCreating } = useCreateExporters('single', {
    onSuccess,
    onError,
  });
  const { mutate: updateExporter, isLoading: isUpdating } = useUpdateExporter(initialValues.id, {
    onSuccess,
    onError,
  });

  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      companyName: Yup.string().required('Required Field'),
      companyPhone: Yup.number().when([], {
        is: () => action === 'create' || action === 'edit',
        then: Yup.number().typeError('should be number').required('Required Field'),
      }),
      firstName: Yup.string().when([], {
        is: () => action === 'create' || action === 'edit',
        then: Yup.string().required('Required Field'),
      }),
      lastName: Yup.string().when([], {
        is: () => action === 'create' || action === 'edit',
        then: Yup.string().required('Required Field'),
      }),
      phoneNumber: Yup.number().when([], {
        is: () => action === 'create' || action === 'edit',
        then: Yup.number().typeError('should be number').required('Required Field'),
      }),
      email: Yup.string()
        .when([], {
          is: () => action === 'create' || action === 'edit',
          then: Yup.string().required('Required Field'),
        })
        .email('Invalid Email'),
      taxID: Yup.string().matches(taxIDRegex, 'Invalid Tax ID format'),
      orgCode: Yup.string(),
      address: Yup.object().shape({
        street: Yup.string().when([], {
          is: () => action === 'create' || action === 'edit',
          then: Yup.string().required('Required Field'),
        }),
        city: Yup.string().when([], {
          is: () => action === 'create' || action === 'edit',
          then: Yup.string().required('Required Field'),
        }),
        state: Yup.string().when([], {
          is: () => action === 'create' || action === 'edit',
          then: Yup.string().required('Required Field'),
        }),
        zip: Yup.string().when([], {
          is: () => action === 'create' || action === 'edit',
          then: Yup.string().required('Required Field'),
        }),
        country: Yup.string().when([], {
          is: () => action === 'create' || action === 'edit',
          then: Yup.string().required('Required Field'),
        }),
        setAsConsignee: Yup.boolean(),
      }),
    });
  }, [action]);

  const onSubmit = (values) => {
    if (action === 'create' || action === 'quick-create') {
      createExporter(values);
    } else if (action === 'edit') {
      updateExporter(values);
    }
  };

  const form = useFormik({
    onSubmit,
    validationSchema,
    initialValues: {
      companyName: initialValues.companyName || '',
      companyPhone: initialValues.companyPhone || '',
      firstName: initialValues.user?.firstName || '',
      lastName: initialValues.user?.lastName || '',
      phoneNumber: initialValues.user?.phoneNumber || '',
      email: initialValues.user?.email || '',
      address: {
        street: initialValues?.user?.street1 || '',
        city: initialValues?.user?.city1 || '',
        state: initialValues?.user?.state1 || '',
        zip: initialValues?.user?.zip1 || '',
        country: CANADA_ALPHA_2,
      },
      taxID: initialValues.taxID || '',
      orgCode: initialValues.user?.orgCode || '',
      setAsConsignee: false,
    },
  });

  return [form, isCreating || isUpdating];
}
